import type { Router } from "vue-router";
import { userSidebarStore } from "@/common/stores/SidebarStore.ts";
import { useLoginStore } from "@/common/stores/LoginStore.ts";
import { nextTick } from "vue";
import { setUserSidebar } from "@/common/plugins/route.ts";
import { verifyRouter, verifySession } from "@/common/api/login/LoginApi.ts";

// [추가] 메뉴 스토어 import (경로가 다르면 수정해주세요)
import { useMenuStore, type MenuItem } from "@/common/stores/useMenuStore";

export const navigationGuard = (router: Router): void => {
  // 앱 타이틀(환경변수) — document.title 뒤에 붙임
  const appTitle = import.meta.env.VITE_APP_TITLE;
  const sidebarStore = userSidebarStore();
  const loginStore = useLoginStore();

  // [추가] 메뉴 스토어 인스턴스
  const menuStore = useMenuStore();

  // app load후 첫 진입때 세션체크
  // 클라이언트 페이지 경로 (로그인 없이 접근 가능)
  const clientPaths = ["/", "/main", "/board", "/centerIntro", "/majorFree", "/ecpIntro"];
  const isClientPath = (path: string) => clientPaths.some((p) => path.startsWith(p));

  // [추가] 재귀적으로 메뉴 트리를 탐색하여 useYn='N'인 메뉴가 있는지 확인하는 함수
  const findBlockedMenu = (menus: MenuItem[], targetPath: string): MenuItem | null => {
    for (const menu of menus) {
      // 1. URL 매칭 확인 (정확히 일치하거나, 하위 경로인 경우)
      // 예: /centerIntro/greeting (target)이 /centerIntro (menu)에 속하는지 확인
      if (menu.url && targetPath.startsWith(menu.url)) {
        // 단순 접두사 매칭 오류 방지 (예: /menu1 vs /menu12)
        // targetPath가 menu.url과 같거나, menu.url + '/'로 시작해야 함
        if (targetPath === menu.url || targetPath.startsWith(`${menu.url}/`)) {
          // 2. 현재 메뉴가 사용 불가(N)이면 반환
          if (menu.useYn === "N") {
            return menu;
          }
        }
      }

      // 3. 자식 메뉴가 있다면 재귀 탐색
      if (menu.children && menu.children.length > 0) {
        const found = findBlockedMenu(menu.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  router.beforeEach(async (to, _from, next) => {
    // 전역 로딩
    window.$loading.show();
    const toMeta = to.meta;
    // console.info("🚀", _from.fullPath, "→", to.fullPath);

    // 1) 로그인 및 에러페이지는 바로 통과
    if (to.name === "login" || to.name === "noServer" || to.name === "error") {
      return next();
    }

    // 2) 클라이언트 페이지 권한 체크 (수정됨)
    if (isClientPath(to.path)) {
      // [수정] 무조건 통과하기 전에 'useYn' 체크 로직 수행

      // 2-1. 메뉴 데이터 로드 (새로고침 시 Store가 비어있을 수 있음)
      // fetchMenus 내부의 중복 호출 방지 로직 덕분에 안전함
      await menuStore.fetchMenus();

      // 2-2. 차단된 메뉴인지 검사
      const blockedMenu = findBlockedMenu(menuStore.menuItems, to.path);

      if (blockedMenu) {
        // 차단된 경우 알림 및 리다이렉트
        alert("현재 제공되지 않는 메뉴이거나 접근이 제한되었습니다.");

        // 현재 위치가 메인이 아니면 메인으로, 이미 메인이면 이동 취소
        if (_from.path !== "/main") {
          return next("/main");
        } else {
          return next(false);
        }
      }

      // 2-3. 차단되지 않았으면 통과
      return next();
    }

    // 3) 세션 검증이 없으면 세션 검증 수행(새로고침 및 첫진입)
    if (!loginStore.getSessionChk) {
      const res = await verifySession();
      if (res != null) loginStore.setLoginInfo(res);
      loginStore.setSessionChk(true);
    }

    // 4) 사용자 라우트가 아직 등록되지 않았으면 등록 수행 (관리자/마이페이지 등)
    if (!sidebarStore.getRouteYn) {
      await setUserSidebar();
    }

    // 5) 정적 라우트는 권한 체크 없이 통과
    if (Object.keys(toMeta).length !== 0 && !toMeta.isDynamic) {
      return next();
    }

    // 6) 서버에 이 라우트에 대한 권한이 있는지 확인
    const res = await verifyRouter({ url: to.path, menuCd: toMeta.menuCd });
    if (res) {
      await loginStore.setApiAuth(res);
      console.info("[Allowed method in '" + String(to.path) + "'] " + loginStore.getApiAuth);
    }
    return next();
  });

  router.afterEach(async (to) => {
    const toMeta = to.meta;
    // 페이지 타이틀 기본값 설정(타이틀 + 앱이름)
    let title = toMeta.title ? toMeta.title + " - " : "";

    // 세션 언어가 영문이면 가능하면 titleEn 사용
    if (sessionStorage.getItem("lang") === "en" && toMeta.titleEn) {
      title = toMeta.titleEn + " - ";
    }
    document.title = title + appTitle;

    /** baseBreadcrumb setting start */
    sidebarStore.setBreadcrumbs([]);
    if (!toMeta.noCrumb) {
      // matched에 있는 라우트 메타들 중 title만 추출
      let matchedTitles = to.matched
        .map((record) => record.meta.title)
        .filter((title): title is string => !!title);
      // 영문 모드이면 titleEn으로 대체 추출
      if (sessionStorage.getItem("lang") === "en" && toMeta.titleEn) {
        matchedTitles = to.matched
          .map((record) => record.meta.titleEn)
          .filter((titleEn): titleEn is string => !!titleEn);
      }
      // 추출한 타이틀들로 breadcrumb 객체 배열 생성(마지막 항목은 disabled)
      const breadcrumbs = matchedTitles.map((title, index) => ({
        title,
        disabled: index === matchedTitles.length - 1,
      })) as [];
      sidebarStore.setBreadcrumbs(breadcrumbs);
    }
    // DOM 업데이트 / 상태 반영 대기
    await nextTick();
    // 전역 로딩 숨김
    window.$loading.hide();
  });
};
